import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { errorTypes } from '@/helpers/constants';
import { validateFeedbackForm } from '@/helpers/subCampaignValidations';
import { isVansBeforeFlowRemoval } from '@/helpers/utils';
import { setSubCampaignFeedback, setValidations } from '@/redux/SubCampaign/subCampaign.action';
import campaignServices from '@/services/campaignServices';
import { getAttribute } from './useCampaignValidate';
import useToastContext from './useToastContext';

const useSubCampaignValidate = (hasToSetSubCR = true) => {
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { subCampaign, subCampaignInfo, validations, isVANCR, version, subCRId } = useSelector((state) => {
    return ({
      subCampaign: state.subCampaignReducer,
      subCampaignInfo: state.subCampaignReducer.subCampaignInfo,
      validations: state.subCampaignReducer.validations,
      isVANCR: isVansBeforeFlowRemoval(state.campaignReducer?.info?.type, state.campaignReducer?.info?.creationDate),
      version: state.subCampaignReducer?.subCampaignInfo?.version,
      subCRId: state.subCampaignReducer.subCampaignInfo?.id,
    });
  });
  const newStateCampaign = { ...subCampaign };

  const validateForms = async (form, data, languageCode, hasToSet) => {
    let newStateWithData = { ...data };

    if (!newStateWithData?.feedback?.id) {
      const stateData = await campaignServices.getSubCampaign(subCRId, `${isVANCR ? 'van_' : ''}feedback`, version);
      newStateWithData = {
        ...newStateWithData,
        feedback: stateData.module,
      };
    }

    switch (form) {
      case 'feedback':
        if (hasToSet) dispatch(setSubCampaignFeedback(newStateWithData.feedback));

        return validateFeedbackForm(validations?.[form]?.[languageCode], newStateWithData);
      default:
        break;
    }
  };

  const validateTab = async (form, acc, languageCode) => ({
    ...acc.validations,
    [getAttribute(form)]: {
      ...acc?.validations?.[getAttribute(form)],
      [languageCode]: await validateForms(form, newStateCampaign, languageCode, hasToSetSubCR),
    },
  });

  const validateTabs = async (languageCode, oldValidations) => {
    const tabsToValidate = ['feedback'];
    const newState = await tabsToValidate.reduce(
      (previousPromise, form) => previousPromise.then((acc) => validateTab(form, acc, languageCode).then((newValidations) => ({
        ...acc,
        ...newValidations,
      }))),
      Promise.resolve({ ...oldValidations }),
    );

    return newState;
  };

  const showMissingMandatoryFieldBanner = (newState) => {
    const tabs = ['feedback'];
    const someMissingFieldInTab = tabs.some(
      (current) => newState?.[current]?.[subCampaignInfo.plantLanguage]?.invalid,
    );

    const incorrectRecipientsOrder = newState?.recipients?.[subCampaignInfo.defaultLanguage]?.incorrectOrder;

    if (incorrectRecipientsOrder) {
      addToast(t('alert.incorrectRecipientsOrder'), 'Warning');
    }
    if (someMissingFieldInTab) addToast(t('alert.mandatoryFieldsEmpty'), 'Warning', errorTypes.mandatory_fields_empty);
  };

  const showDuplicateCampaignBanner = (newState) => {
    const someDuplicateCampaign = Object.keys(newState).some(
      (currentTab) => Object.keys(newState[currentTab]).some(
        (currentLanguage) => Object.keys(newState[currentTab][currentLanguage].campaigns).some(
          (currentCampaign) => newState?.[currentTab]?.[currentLanguage]?.campaigns?.[currentCampaign]?.campaignNumber?.errors?.duplicate,
        ),
      ),
    );

    if (someDuplicateCampaign) {
      addToast(t('alert.duplicateCampaign'), hasToSetSubCR ? 'Error' : 'Warning', 'DUPLICATE_CAMPAIGN_SUBCR');
    }
  };

  const validateLanguages = (tabsToValidate) => subCampaignInfo.activeLanguages.map((languageCode) => {
    const isValid = Object.keys(tabsToValidate).every(
      (currentTab) => !tabsToValidate[currentTab]?.[languageCode]?.invalid,
    );

    return { language: languageCode, isValid };
  });

  const validateSubCampaign = async () => {
    const newState = await subCampaignInfo.activeLanguages.reduce(
      (previousPromise, languageCode) => previousPromise.then((acc) => validateTabs(languageCode, acc).then((newValidations) => ({
        ...acc,
        ...newValidations,
      }))),
      Promise.resolve({ ...validations }),
    );
    const languagesValidations = validateLanguages(newState);
    showMissingMandatoryFieldBanner(newState);
    showDuplicateCampaignBanner(newState);
    dispatch(setValidations(newState));

    return languagesValidations?.every(({ isValid }) => isValid);
  };

  return validateSubCampaign;
};

export default useSubCampaignValidate;
